import React, { useEffect, useState, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactPopup from '../components/ContactPopup';
import { Users, Target, Award, TrendingUp, Heart, Lightbulb, Star, BookOpen, ArrowRight, Zap } from 'lucide-react';

const About = () => {
  // Popup state management
  const [popupState, setPopupState] = useState({
    isOpen: false,
    title: "Get in Touch",
    subtitle: "Connect with our admissions team to learn more about our programs",
    formType: "popup_inquiry",
    interestedProgram: ""
  });

  const openPopup = (config: {
    title?: string;
    subtitle?: string;
    formType?: string;
    interestedProgram?: string;
  } = {}) => {
    setPopupState({
      isOpen: true,
      title: config.title || "Get in Touch",
      subtitle: config.subtitle || "Connect with our admissions team to learn more about our programs",
      formType: config.formType || "popup_inquiry",
      interestedProgram: config.interestedProgram || ""
    });
  };

  const closePopup = () => {
    setPopupState(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { number: "10+", value: 10, suffix: "+", label: "Years of industry expertise" },
    { number: "80%", value: 80, suffix: "%", label: "Of graduates reported satisfaction" },
    { number: "175k+", value: 175, suffix: "k+", label: "Learner milestones supported" },
    { number: "95%", value: 95, suffix: "%", label: "Of alumni recommend our program" }
  ];

  // Animated Counter Component
  const AnimatedCounter = ({ targetValue, suffix, duration = 2000 }: { targetValue: number; suffix: string; duration?: number }) => {
    const [count, setCount] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const counterRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
            
            // Start animation
            const startTime = Date.now();
            const startValue = 0;
            
            const updateCount = () => {
              const currentTime = Date.now();
              const elapsed = currentTime - startTime;
              const progress = Math.min(elapsed / duration, 1);
              
              // Easing function for smooth animation
              const easeOutQuart = 1 - Math.pow(1 - progress, 4);
              const currentCount = Math.floor(startValue + (targetValue - startValue) * easeOutQuart);
              
              setCount(currentCount);
              
              if (progress < 1) {
                requestAnimationFrame(updateCount);
              }
            };
            
            requestAnimationFrame(updateCount);
          }
        },
        { threshold: 0.5 }
      );

      if (counterRef.current) {
        observer.observe(counterRef.current);
      }

      return () => {
        if (counterRef.current) {
          observer.unobserve(counterRef.current);
        }
      };
    }, [targetValue, duration, isVisible]);

    return (
      <div ref={counterRef} className="text-3xl sm:text-4xl font-bold text-blue-600 mb-2">
        {count}{suffix}
      </div>
    );
  };

  const values = [
    { 
      title: "Embrace change fearlessly", 
      description: "Technology evolves rapidly, and so should you. We encourage our students to adapt, innovate, and stay ahead of industry trends with confidence.",
      icon: <TrendingUp className="w-7 h-7" />,
      color: "blue"
    },
    { 
      title: "Cultivate tech confidence", 
      description: "From your first line of code to complex AI models, we build your technical confidence through hands-on practice and expert mentorship.",
      icon: <Zap className="w-7 h-7" />,
      color: "yellow"
    },
    { 
      title: "Bring out the best in you", 
      description: "Every student has unique strengths. Our personalized approach helps you discover and amplify your potential in the tech world.",
      icon: <Star className="w-7 h-7" />,
      color: "purple"
    },
    { 
      title: "Champion lifelong learning", 
      description: "The tech journey never ends. We instill a growth mindset that keeps you curious, learning, and evolving throughout your career.",
      icon: <BookOpen className="w-7 h-7" />,
      color: "green"
    },
    { 
      title: "Bridge industry divides", 
      description: "Whether you're from finance, healthcare, or any other field, we help you transition seamlessly into tech roles with transferable skills.",
      icon: <ArrowRight className="w-7 h-7" />,
      color: "indigo"
    },
    { 
      title: "Transform theory into practice", 
      description: "Real-world projects, industry case studies, and practical applications ensure you graduate with practical confidence, not just theory-heavy knowledge.",
      icon: <Target className="w-7 h-7" />,
      color: "red"
    }
  ];

  const team = [
    { 
      name: "Arjun Maheshwari", 
      role: "Co-Founder", 
      linkedin: "https://www.linkedin.com/in/arjun59",
      image: "/arjun-maheshwari.jpg"
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F1EFED' }}>
      <Header openPopup={openPopup} />
      
      {/* Hero Section */}
      <div className="py-16 sm:py-20" style={{ backgroundColor: '#F1EFED' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-8 tracking-tight leading-tight">
              At <span className="text-blue-600">1to10x</span>, career evolution<br />
              isn't a goal. It's our <span className="text-blue-600">obsession</span>.
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 max-w-5xl mx-auto leading-relaxed">
              1to10x is not your typical educational platform. With professionals having experience worth more than a decade, we have focused on one simple thing: creating delightful learning experiences that power our students' digital transformation.
            </p>
            <p className="text-base sm:text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed mt-6">
              1to10x is a public-facing brand name used for programs and services offered through this website. <span className="font-semibold text-gray-900">ANM ENABLERS PRIVATE LIMITED</span> and <span className="font-semibold text-gray-900">1to10X LLC</span> provide such programs and services, as applicable, for their respective operations and obligations.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        
        {/* Program Overview & Vision */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          <div className="bg-white rounded-lg p-8 border border-gray-200">
            <div className="flex items-center space-x-3 mb-6">
              <Target className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Program <span className="text-blue-600">Overview</span></h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              1to10x empowers professionals to become innovative tech leaders. We break down barriers to entry in the tech industry, providing expert-led training and the confidence to leap into thriving tech careers, no matter where they're starting from.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 border border-gray-200">
            <div className="flex items-center space-x-3 mb-6">
              <Lightbulb className="w-6 h-6 text-purple-600" />
              <h2 className="text-2xl font-bold text-gray-900">Our <span className="text-purple-600">Vision</span></h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              We envision a future where anyone with drive can shape the future through technology. In this future, we aim to build a global network of adaptable tech professionals who lead with emotional intelligence, communicate clearly, and use tech to solve big problems for everyone.
            </p>
          </div>
        </div>

        {/* Future Building Section */}
        <div className="bg-white rounded-lg p-8 sm:p-12 border border-gray-200 mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              🚀 We build the future of <span className="text-blue-600">tech careers</span>.
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              We're built on a foundation of expertise and a vision for the future. Our approach to education in Data Science and AI is revolutionary because it's rooted in years of industry insight. We don't just teach – we transform. Our meticulously crafted programs are designed to turn ambitious professionals into sought-after data scientists and AI specialists. With each cohort, we're not just sharing knowledge; we're shaping the next generation of tech innovators.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg border border-gray-100">
                <AnimatedCounter targetValue={stat.value} suffix={stat.suffix} duration={2000} />
                <div className="text-gray-600 text-sm leading-relaxed">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Mission & Values */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Our <span className="text-blue-600">core values</span> drive everything we do
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              These principles guide our approach to education, mentorship, and career transformation. They're not just words on a page – they're the foundation of every interaction, every lesson, and every success story.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const getColorClasses = (color: string) => {
                const colors: { [key: string]: { bg: string; icon: string; border: string } } = {
                  blue: { bg: "bg-blue-50", icon: "text-blue-600", border: "border-blue-200" },
                  yellow: { bg: "bg-yellow-50", icon: "text-yellow-600", border: "border-yellow-200" },
                  purple: { bg: "bg-purple-50", icon: "text-purple-600", border: "border-purple-200" },
                  green: { bg: "bg-green-50", icon: "text-green-600", border: "border-green-200" },
                  indigo: { bg: "bg-indigo-50", icon: "text-indigo-600", border: "border-indigo-200" },
                  red: { bg: "bg-red-50", icon: "text-red-600", border: "border-red-200" }
                };
                return colors[color] || colors.blue;
              };

              const colorClasses = getColorClasses(value.color);

              return (
                <div key={index} className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <div className={`w-16 h-16 ${colorClasses.bg} rounded-xl flex items-center justify-center mb-6 ${colorClasses.border} border-2`}>
                    <div className={colorClasses.icon}>{value.icon}</div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 leading-tight">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>

          {/* Additional Mission Statement */}
          <div className="mt-16 bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-8 sm:p-12 border border-gray-200">
            <div className="text-center">
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                Our <span className="text-green-600">empathy</span> drives us, our <span className="text-blue-600">innovation</span> sets us apart
              </h3>
              <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
                We understand that career transformation isn't just about learning new skills – it's about overcoming fears, building confidence, and navigating a completely new professional landscape. That's why we combine cutting-edge curriculum with genuine human support.
              </p>
              <div className="grid md:grid-cols-3 gap-8 mt-12 pt-8">
                <div className="text-center bg-white/50 rounded-xl p-6 border border-gray-100 hover:shadow-md transition-all duration-300">
                  <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Heart className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-3 text-lg">Empathy-First Approach</h4>
                  <p className="text-gray-600 text-base leading-relaxed">We understand your journey because we've been there too</p>
                </div>
                <div className="text-center bg-white/50 rounded-xl p-6 border border-gray-100 hover:shadow-md transition-all duration-300">
                  <div className="w-16 h-16 bg-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Lightbulb className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-3 text-lg">Innovation-Driven</h4>
                  <p className="text-gray-600 text-base leading-relaxed">Cutting-edge methods that actually work in the real world</p>
                </div>
                <div className="text-center bg-white/50 rounded-xl p-6 border border-gray-100 hover:shadow-md transition-all duration-300">
                  <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-3 text-lg">Results-Focused</h4>
                  <p className="text-gray-600 text-base leading-relaxed">Your success is our only measure of success</p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Full-width separator line - edge to edge */}
      <div className="w-full h-px bg-gray-300"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-20">

        {/* Team Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              ✨ Meet the <span className="text-blue-600">team</span>
            </h2>
            <div className="max-w-5xl mx-auto space-y-6 text-lg text-gray-600 leading-relaxed">
              <p>
                1to10x is a community of dedicated professionals transforming careers through cutting-edge AI and Data Science education. We believe learning should be a chance to grow, innovate, and impact the tech world. Our team collaborates to guide students through the rapidly evolving tech landscape, sharing knowledge and experiences.
              </p>
              <p>
                Excellence drives our mission. Our carefully selected instructors and mentors, all industry veterans, provide transformative guidance. Known for our unique approach, we avoid generic courses and one-size-fits-all solutions, ensuring each student receives tailored, high-quality education to thrive in AI and Data Science.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 border border-gray-200 text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group relative overflow-hidden">
                {/* Background gradient effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative z-10">
                  {/* Profile Image */}
                  <div className="w-48 h-48 mx-auto mb-6 group-hover:scale-105 transition-transform duration-300">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/placeholder.svg';
                      }}
                      className="w-full h-full rounded-2xl object-cover shadow-2xl border-4 border-white group-hover:border-blue-200 transition-colors duration-300"
                    />
                  </div>
                  
                  {/* Name and Role */}
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                    {member.name}
                  </h3>
                  <p className="text-blue-600 font-medium mb-6 text-sm uppercase tracking-wide">
                    {member.role}
                  </p>
                  
                  {/* LinkedIn Button */}
                  <a 
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    Connect on LinkedIn
                  </a>
                  
                  {/* Decorative element */}
                  <div className="absolute -bottom-2 -right-2 w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-300"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mission Statement Section */}
        <div className="bg-white rounded-lg p-8 sm:p-12 border border-gray-200 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">
            Our <span className="text-blue-600">Mission</span>
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-6">
            <p className="text-xl text-gray-700 leading-relaxed">
              We believe technology should be accessible to everyone, not just those born into it. Our mission is to break down barriers and transform passionate professionals into skilled innovators who shape the future.
            </p>
            
            <p className="text-lg text-gray-600 leading-relaxed">
              Through personalized learning, expert mentorship, and real-world application, we don't just teach technology – we build the confidence and connections that turn career dreams into reality.
            </p>
            
            <div className="pt-4">
              <p className="text-2xl font-bold text-blue-600 italic">
                "Building tomorrow's tech leaders, today."
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      
      <ContactPopup
        isOpen={popupState.isOpen}
        onClose={closePopup}
        title={popupState.title}
        subtitle={popupState.subtitle}
        formType={popupState.formType}
        interestedProgram={popupState.interestedProgram}
      />
    </div>
  );
};

export default About; 
